/**
 * Copyright 2026, IntraLinks, Inc. All rights reserved.
 */

import React, {
  ChangeEvent, FormEvent, useEffect, useState
} from 'react';
import {
  IlButton, IlInlineLoading, IlTextInput, IlInlineNotification, IlLoading
} from 'il-framework-component';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { useLocation } from 'react-router';
import { DEFAULT_SMS_DIGITS, I18nNamespaces, LOA_RESEND_SECONDS } from '../../../../app/shared/utils/constants.util';
import { RootState } from '../../../../app/store';
import { AuthErrorKey, SecuritySetupType } from '../../../../domain/models/impl/SecuritySetup';
import { getLoaValidationMessageKey, useRequiredLevel } from '../../../../app/shared/utils/helpers.util';
import usePreserveSearchNav from '../../../../app/shared/hooks/usePreserveSearchNav';
import {
  setError,
  setValidationError,
  verifyOrActivateFactor,
  setSelectedFactor,
  challengeFactor,
  loadSecuritySetupPage,
  selectFactorForVerify
} from '../../setupSecuritySlice';
import { SecuritySetupRoutes } from '../../../../app/shared/utils/securitySetupRoutes';

import styles from './VerifySMS.scss';

const VerifySMS: React.FC = () => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const requiredLevel = useRequiredLevel();
  const { goBackOrReplace } = usePreserveSearchNav();
  const { search } = useLocation();
  const factorIdFromUrl = new URLSearchParams(search || '').get('factorId') ?? '';

  const enrolled = useSelector((s: RootState) => s.setupSecurity.enrolledFactors);
  const actionLoading = useSelector((s: RootState) => s.setupSecurity.actionLoading);
  const validationError = useSelector((s: RootState) => s.setupSecurity.validationError);
  const smsErrorKey = useSelector((s: RootState) => s.setupSecurity.error);
  const selectedFactor = useSelector((s: RootState) => s.setupSecurity.selectedFactor);
  const pageLoading = useSelector((s: RootState) => s.setupSecurity.pageLoading);

  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState<number>(0);
  const [hasChallenged, setHasChallenged] = useState(false);

  const factorId = selectedFactor?.factorId ?? '';
  const phone = (selectedFactor?.data ?? '') as string;
  const digits = selectedFactor?.digits ?? DEFAULT_SMS_DIGITS;
  const canVerify = Boolean(factorId) && digits > 0;
  const typedDigits = code.replace(/\D/g, '').length;

  const canSubmit = canVerify
    && !actionLoading
    && !validationError
    && typedDigits === digits;

  const shouldShowInlineNotification = !!smsErrorKey;

  const canResend = Boolean(factorId) && resendIn === 0 && !actionLoading;
  const challengedOnceRef = React.useRef<Record<string, boolean>>({});

  const inlineTitleMapVerifySMS: Record<string, string> = {
    [AuthErrorKey.DefaultSMSChallengeError]: t(
      'loaEnrichment.common.errors.challengeFailedInline',
      { ns: I18nNamespaces.LOA_ENRICHMENT }
    ),
    [AuthErrorKey.DefaultSMSVerifyError]: t(
      'loaEnrichment.factors.sms.inline.verifyFailed',
      { ns: I18nNamespaces.LOA_ENRICHMENT }
    )
  };

  const inlineTitleVerifySMS = inlineTitleMapVerifySMS[smsErrorKey]
    ?? t('loaEnrichment.common.errors.challengeFailedInline', { ns: I18nNamespaces.LOA_ENRICHMENT });

  useEffect(() => {
    const bootstrap = async () => {
      if (!factorIdFromUrl) {
        goBackOrReplace(SecuritySetupRoutes.root);
        return;
      }

      if (!enrolled.length) {
        await dispatch(loadSecuritySetupPage(t) as any);
      }

      dispatch(selectFactorForVerify(factorIdFromUrl));
    };

    bootstrap();
  }, [dispatch, factorIdFromUrl, enrolled.length, t, goBackOrReplace]);

  const sendCode = async () => {
    if (!factorId) return;
    if (selectedFactor?.type !== SecuritySetupType.SMS) return;

    const shouldStartCooldown = await dispatch(challengeFactor(
      t,
      {
        factorId,
        factorType: SecuritySetupType.SMS,
        errorKey: AuthErrorKey.DefaultSMSChallengeError,
        toastKey: 'loaEnrichment.common.errors.sendFailedToast'
      }
    ) as any);
    if (shouldStartCooldown) {
      setHasChallenged(true);
      setResendIn(LOA_RESEND_SECONDS);
    }
  };

  useEffect(() => {
    if (!selectedFactor) return;
    if (challengedOnceRef.current[factorId]) return;

    challengedOnceRef.current[factorId] = true;

    if (selectedFactor.type !== SecuritySetupType.SMS) {
      goBackOrReplace(SecuritySetupRoutes.root);
      return;
    }

    if (!factorId) return;
    if (hasChallenged) return;

    sendCode();
  }, [selectedFactor, factorId, hasChallenged, goBackOrReplace]);

  useEffect(() => {
    if (resendIn <= 0) {
      return undefined;
    };

    const id = window.setInterval(() => {
      setResendIn((s) => Math.max(0, s - 1));
    }, 1000);

    return () => window.clearInterval(id);
  }, [resendIn]);

  useEffect(() => () => {
    dispatch(setError(''));
    dispatch(setValidationError(''));
    dispatch(setSelectedFactor(undefined));
  }, [dispatch]);

  const updateCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setError(''));
    dispatch(setValidationError(''));

    const input = e.currentTarget.value.replace(/\D/g, '');
    setCode(input.slice(0, digits));
  };

  const submit = (ev: FormEvent | ChangeEvent) => {
    ev.preventDefault();
    if (!factorId) return;

    dispatch(verifyOrActivateFactor(t, {
      factorId,
      code: code.replace(/\s/g, ''),
      action: 'verify',
      successToastMsg: 'loaEnrichment.common.messages.verified',
      elevateLevel: selectedFactor?.loaLevel ?? requiredLevel
    }));
  };

  return (
    pageLoading ? <IlLoading withOverlay /> : (
      <>
        <h1 className={classnames(styles.formTitle, styles.title)}>
          {t('loaEnrichment.common.titles.verifyIdentity')}
        </h1>
        <p className={styles.helpText}>
          {t('loaEnrichment.factors.sms.verifyHelpText', { phoneNumber: phone })}
        </p>
        {shouldShowInlineNotification && (
          <div className={styles.inlineNotification}>
            <IlInlineNotification
              title={inlineTitleVerifySMS}
              notificationType='inline'
              kind='error'
              hideCloseButton
            />
          </div>
        )}

        <form onSubmit={submit}>
          <IlTextInput
            id='sms-verification-code'
            labelText={t('loaEnrichment.common.labels.verificationCode')}
            placeholder={t('loaEnrichment.common.placeholders.code', { digits })}
            type='text'
            maxLength={digits}
            invalid={!!validationError}
            invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
            value={code}
            onChange={updateCode}
            autoFocus
            autoComplete='off'
          />

          <div className={styles.activationContainer}>
            <div className={styles.resendContainer}>
              <p className={styles.resendText}>{t('loaEnrichment.common.resend.smsPrompt')}</p>
              <IlButton
                className={styles.resendBtn}
                kind='ghost'
                type='button'
                disabled={!canResend}
                onClick={sendCode}
              >
                {resendIn > 0
                  ? t('loaEnrichment.common.resend.resendIn', { seconds: resendIn })
                  : t('loaEnrichment.common.buttons.resendCode')}
              </IlButton>
            </div>

            <IlButton
              kind='primary'
              type='submit'
              disabled={!canSubmit}
            >
              {t('loaEnrichment.common.buttons.verify')}
            </IlButton>
          </div>
        </form>

        {actionLoading && (
          <div className={styles.loadingContainer}>
            <IlInlineLoading
              description={t('loaEnrichment.common.status.pleaseWait')}
              success={false}
              onSuccess={noop}
            />
          </div>
        )}
      </>
    )
  );
};

export default VerifySMS;
